/**
 * RESPONSIBILITY: one sermon's own page (R67).
 *
 *   Sermons / <series> / <title>
 *   [ the video — plays here ]
 *   Title
 *   Speaker · date · series · Key scripture
 *   Description
 *   Sermon notes
 *   Application & discussion questions
 *   [ Watch on YouTube ]
 *   More from this series: [card][card][card]
 *
 * Each part is drawn only when it is filled in, so a sermon entered with nothing but a
 * link and a date is still a complete-looking page (R18, R31).
 */
import { Html } from '@framework/react';
import { hasImage, idOf } from '@framework/core';
import type { SermonRow, SermonsSection } from '@app/sections';
import { Picture } from '../commons/Picture';
import { YoutubeThumb } from '../commons/YoutubeThumb';
import { VideoPlayer } from '../commons/VideoPlayer';
import { Crumbs } from '../commons/Crumbs';
import { Page } from '../commons/pages';
import { useWords } from '../commons/words';
import { NotPublic } from '../commons/NotPublic';
import { SermonCard } from './SermonCard';
import { newestFirst } from './sermon-filter';
import { sermonDay, sermonName } from './sermon-day';

export interface SermonDetailProps {
  c: SermonsSection;
  s: SermonRow | undefined;
  /** Every sermon this visitor may see, for "more from this series". */
  all: readonly SermonRow[];
}

export function SermonDetail({ c, s, all }: SermonDetailProps) {
  const w = useWords();
  if (!s) {
    return (
      <>
        <Crumbs trail={[{ label: w.sermonsLabel, page: Page.Sermons }]} />
        <section className="band"><div className="wrap stack">
          <p className="lede">{c.missingText}</p>
          <a className="btn btn-ghost" href="#sermons">{c.backLabel}</a>
        </div></section>
      </>
    );
  }

  const meta = [s.speaker, sermonDay(s.preachedOn), s.series, s.occasion].filter(Boolean);
  const more = s.series
    ? all.filter((x) => x.series === s.series && idOf(x) !== idOf(s)).sort(newestFirst).slice(0, 3)
    : [];
  const picture = hasImage(s.image)
    ? <Picture value={s.image} alt={s.title} className="video-pic" />
    : <YoutubeThumb url={s.youtubeUrl} alt={s.title} className="video-pic" />;

  return (
    <>
      <Crumbs trail={[
        { label: w.sermonsLabel, page: Page.Sermons },
        ...(s.series ? [{ label: s.series }] : []),
        { label: sermonName(s) },
      ]} />
      <section className="band">
        <div className="wrap sermon-page">
          {/* The video plays here, on the page (James); with no link, the picture alone. */}
          {s.youtubeUrl
            ? <VideoPlayer url={s.youtubeUrl} title={sermonName(s)} picture={picture} />
            : hasImage(s.image) && <div className="video">{picture}</div>}
          <div className="sermon-head">
            {s.show === false && <NotPublic />}
            <h1>{sermonName(s)}</h1>
            {meta.length > 0 && <p className="sermon-meta">{meta.join(' · ')}</p>}
            {s.scripture && <p className="sermon-scripture">{s.scripture}</p>}
          </div>
          {s.summaryHtml && <Html className="md-body lede">{s.summaryHtml}</Html>}
          {s.notesHtml && (
            <div className="sermon-part">
              {c.notesHeading && <h2>{c.notesHeading}</h2>}
              <Html className="md-body">{s.notesHtml}</Html>
            </div>
          )}
          {s.questionsHtml && (
            <div className="sermon-part sermon-questions">
              {c.questionsHeading && <h2>{c.questionsHeading}</h2>}
              <Html className="md-body">{s.questionsHtml}</Html>
            </div>
          )}
          {s.youtubeUrl && c.watchLabel && (
            <div className="btns">
              <a className="btn btn-ghost" href={s.youtubeUrl} target="_blank" rel="noopener">{c.watchLabel}</a>
            </div>
          )}
        </div>
      </section>
      {more.length > 0 && (
        <section className="band band-tint">
          <div className="wrap">
            {c.moreHeading && <div className="band-head"><h2>{c.moreHeading}</h2></div>}
            <div className="sermon-grid">{more.map((x) => <SermonCard key={idOf(x)} s={x} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}
